import {
  ChartPoint,
  DashboardTiles,
  LeadPipeline,
  LeadPipelineListItem,
  LeadPipelineListSort,
  LeadPipelineStatus,
  LeadPipelineTabCounts,
  PaginationMeta,
  PipelineActivity,
  PipelineCompany,
  PipelineExport,
  PipelineLead,
  PipelineLeadDetail,
  PipelineNotification,
  PipelinePerson,
  PipelineRun,
  PipelineRunDetail,
  PreviewCountsResponse,
  VeridionFilterPayload,
} from '../../models/lead-pipeline.model';

/** Generic async load lifecycle marker used across every sub-slice. */
export type LoadStatus = 'idle' | 'loading' | 'success' | 'error';

export type WizardMode = 'create' | 'edit';
export type DashboardChartRange = 7 | 30 | 90;
export type LeadsTab = 'new' | 'all' | 'notifications';

/** Empty paginator placeholder for initial state. */
export const EMPTY_META: PaginationMeta = {
  current_page: 1,
  last_page: 1,
  per_page: 25,
  total: 0,
};

// --- Sub-resource filter shapes -------------------------------------------------

export interface ListFiltersState {
  status?: LeadPipelineStatus;
  sort: LeadPipelineListSort;
}

export interface CompaniesFilters {
  q?: string;
  status?: string;
  industry?: string;
  country?: string;
  min_employees?: number;
  max_employees?: number;
  sort?: string;
  current_filters_only?: boolean;
}

export interface PersonsFilters {
  q?: string;
  seniority?: string;
  email_status?: string;
  min_score?: number;
  sort?: string;
  current_filters_only?: boolean;
}

export interface LeadsFilters {
  tab: LeadsTab;
  sort: string;
}

export interface ActivityFilters {
  event_type?: string;
}

export interface ExportEntityCreating {
  entity: string;
  status: LoadStatus;
}

// --- Slice ----------------------------------------------------------------------

export interface LeadPipelineState {
  list: {
    items: LeadPipelineListItem[];
    meta: PaginationMeta;
    tabs: LeadPipelineTabCounts | null;
    status: LoadStatus;
    filters: ListFiltersState;
    includeArchived: boolean;
  };
  detail: {
    current: LeadPipeline | null;
    status: LoadStatus;
  };
  dashboard: {
    tiles: DashboardTiles | null;
    chart: ChartPoint[];
    chartRange: DashboardChartRange;
    status: LoadStatus;
  };
  companies: {
    items: PipelineCompany[];
    meta: PaginationMeta;
    filters: CompaniesFilters;
    status: LoadStatus;
  };
  persons: {
    items: PipelinePerson[];
    meta: PaginationMeta;
    filters: PersonsFilters;
    status: LoadStatus;
    unlockingIds: string[];
  };
  leads: {
    items: PipelineLead[];
    meta: PaginationMeta;
    filters: LeadsFilters;
    status: LoadStatus;
    selectedLead: PipelineLeadDetail | null;
    drawerStatus: LoadStatus;
  };
  activity: {
    items: PipelineActivity[];
    meta: PaginationMeta;
    filters: ActivityFilters;
    status: LoadStatus;
  };
  runs: {
    items: PipelineRun[];
    meta: PaginationMeta;
    status: LoadStatus;
    currentRun: PipelineRunDetail | null;
  };
  notifications: {
    items: PipelineNotification[];
    unreadCount: number;
    status: LoadStatus;
  };
  exports: {
    items: PipelineExport[];
    status: LoadStatus;
    creating: ExportEntityCreating | null;
  };
  wizard: {
    mode: WizardMode;
    editUuid: string | null;
    /** Status of the pipeline being edited; drives the steps 1–2 read-only lock. */
    editStatus: LeadPipelineStatus | null;
    step: 1 | 2 | 3;
    filters: VeridionFilterPayload;
    leadsPerDay: 5 | 10 | null;
    workspaceId: string | null;
    title: string;
    description: string;
    sessionId: string;
    previewCounts: PreviewCountsResponse | null;
    previewStatus: LoadStatus;
    previewError: string | null;
    previewRateLimitRetryAfter: number | null;
    uniquePayloadsSent: number;
    submitting: boolean;
    /** Backend 422 field errors from launch/update, keyed by field name. */
    submitError: Record<string, string[]> | null;
  };
  runNow: {
    inFlight: Record<string, boolean>;
    confirmModalOpen: boolean;
  };
}

export const initialLeadPipelineState: LeadPipelineState = {
  list: {
    items: [],
    meta: { ...EMPTY_META },
    tabs: null,
    status: 'idle',
    filters: { sort: 'newest' },
    includeArchived: false,
  },
  detail: {
    current: null,
    status: 'idle',
  },
  dashboard: {
    tiles: null,
    chart: [],
    chartRange: 30,
    status: 'idle',
  },
  companies: {
    items: [],
    meta: { ...EMPTY_META },
    filters: {},
    status: 'idle',
  },
  persons: {
    items: [],
    meta: { ...EMPTY_META },
    filters: {},
    status: 'idle',
    unlockingIds: [],
  },
  leads: {
    items: [],
    meta: { ...EMPTY_META },
    filters: { tab: 'all', sort: 'newest' },
    status: 'idle',
    selectedLead: null,
    drawerStatus: 'idle',
  },
  activity: {
    items: [],
    meta: { ...EMPTY_META },
    filters: {},
    status: 'idle',
  },
  runs: {
    items: [],
    meta: { ...EMPTY_META },
    status: 'idle',
    currentRun: null,
  },
  notifications: {
    items: [],
    unreadCount: 0,
    status: 'idle',
  },
  exports: {
    items: [],
    status: 'idle',
    creating: null,
  },
  wizard: {
    mode: 'create',
    editUuid: null,
    editStatus: null,
    step: 1,
    filters: [],
    leadsPerDay: 5,
    workspaceId: null,
    title: '',
    description: '',
    sessionId: '',
    previewCounts: null,
    previewStatus: 'idle',
    previewError: null,
    previewRateLimitRetryAfter: null,
    uniquePayloadsSent: 0,
    submitting: false,
    submitError: null,
  },
  runNow: {
    inFlight: {},
    confirmModalOpen: false,
  },
};
